import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { POSAdminLayout } from '@/features/pos/admin/components/POSAdminLayout';
import { useDiningAreas, useCreateDiningArea, useUpdateDiningArea, useDeleteDiningArea } from '@/features/pos/hooks/useDiningAreas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Loader2, Plus, MapPin, Edit, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from '@/components/ui/toast/useToast';
import { DiningAreaFormData, DiningArea } from '@/features/pos/types/admin';

export const Route = createFileRoute('/apps/pos/admin/areas/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: AreasPage,
});

function AreasPage() {
  const { data: areas = [], isLoading } = useDiningAreas();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editArea, setEditArea] = useState<DiningArea | null>(null);

  if (isLoading) {
    return (
      <POSAdminLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </POSAdminLayout>
    );
  }

  return (
    <POSAdminLayout>
      <div className="p-8">
        <div className="max-w-6xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <MapPin className="w-8 h-8 text-emerald-600" />
                <h1 className="text-3xl font-bold">Dining Areas</h1>
              </div>
              <p className="text-gray-600">Manage restaurant areas and rooms</p>
            </div>
            <Button onClick={() => setCreateModalOpen(true)} size="lg">
              <Plus className="w-4 h-4 mr-2" />
              New Area
            </Button>
          </div>

          {areas.length === 0 ? (
            <Card className="p-12 text-center">
              <MapPin className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold mb-2">No areas yet</h3>
              <p className="text-gray-500 mb-4">Create your first dining area</p>
              <Button onClick={() => setCreateModalOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Area
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {areas.map((area) => (
                <AreaCard key={area.id} area={area} onEdit={setEditArea} />
              ))}
            </div>
          )}
        </div>
      </div>

      <AreaModal 
        open={createModalOpen} 
        onClose={() => setCreateModalOpen(false)} 
      />
      
      {editArea && (
        <AreaModal 
          open={true} 
          onClose={() => setEditArea(null)} 
          area={editArea} 
        />
      )}
    </POSAdminLayout>
  );
}

function AreaCard({ area, onEdit }: { area: DiningArea; onEdit: (area: DiningArea) => void }) {
  const deleteArea = useDeleteDiningArea();

  const handleDelete = async () => {
    if (confirm(`Delete area "${area.name}"?`)) {
      try {
        await deleteArea.mutateAsync(area.id);
        toast.success('Deleted', 'Area deleted successfully');
      } catch (error: any) {
        toast.error('Error', error.message);
      }
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div 
            className="w-12 h-12 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: area.color + '20' }}
          >
            <MapPin className="w-6 h-6" style={{ color: area.color }} />
          </div>
          <div>
            <h3 className="font-semibold">{area.name}</h3>
            {area.name_ar && <p className="text-sm text-gray-500">{area.name_ar}</p>}
          </div>
        </div>
      </div>
      
      <div className="space-y-2 text-sm text-gray-600 mb-4">
        <p>Type: <span className="capitalize">{area.area_type}</span></p>
        {area.capacity && <p>Capacity: {area.capacity} people</p>}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => onEdit(area)} className="flex-1">
          <Edit className="w-4 h-4 mr-1" />
          Edit
        </Button>
        <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-600 hover:bg-red-50">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
}

function AreaModal({ open, onClose, area }: { open: boolean; onClose: () => void; area?: DiningArea }) {
  const createArea = useCreateDiningArea();
  const updateArea = useUpdateDiningArea();

  const { register, handleSubmit, watch, setValue, reset } = useForm<DiningAreaFormData>({
    defaultValues: area || {
      name: '',
      name_ar: '',
      area_type: 'indoor',
      color: '#10B981',
    },
  });

  const onSubmit = async (data: DiningAreaFormData) => {
    try {
      if (area) {
        await updateArea.mutateAsync({ id: area.id, data });
        toast.success('Updated', 'Area updated successfully');
      } else {
        await createArea.mutateAsync(data);
        toast.success('Created', 'Area created successfully');
        reset();
      }
      onClose();
    } catch (error: any) {
      toast.error('Error', error.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{area ? 'Edit Area' : 'New Area'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label>Name *</Label>
            <Input {...register('name', { required: true })} placeholder="Main Dining" />
          </div>
          <div>
            <Label>Arabic Name</Label>
            <Input {...register('name_ar')} placeholder="قاعة الطعام الرئيسية" dir="rtl" />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={watch('area_type')} onValueChange={(v: any) => setValue('area_type', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="indoor">Indoor</SelectItem>
                <SelectItem value="outdoor">Outdoor</SelectItem>
                <SelectItem value="terrace">Terrace</SelectItem>
                <SelectItem value="private">Private Room</SelectItem>
                <SelectItem value="bar">Bar</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Capacity (people)</Label>
            <Input type="number" {...register('capacity', { valueAsNumber: true })} placeholder="50" />
          </div>
          <div>
            <Label>Color</Label>
            <div className="flex gap-2">
              <Input type="color" {...register('color')} className="w-20" />
              <Input value={watch('color')} onChange={(e) => setValue('color', e.target.value)} placeholder="#10B981" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={createArea.isPending || updateArea.isPending}>
              {(createArea.isPending || updateArea.isPending) ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
              ) : (
                <>{area ? 'Update' : 'Create'}</>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
